/**
 * Розбір CSV для переглядача диска (`DISK-001` §4.7): роздільник — кома,
 * крапка з комою або табуляція (визначається автоматично), поля в подвійних
 * лапках можуть містити роздільник, переведення рядка й подвоєні лапки `""`.
 */

export type CsvDelimiter = ',' | ';' | '\t'

const CANDIDATES: CsvDelimiter[] = [',', ';', '\t']

/** Скільки рядків файлу дивимось, щоб визначити роздільник. */
const SAMPLE_LINES = 20

/** Кількість роздільника в кожному рядку зразка — поза лапками. */
const countPerLine = (sample: string, delimiter: CsvDelimiter): number[] => {
    const counts: number[] = []
    let quoted = false
    let count = 0

    for (let index = 0; index < sample.length; index += 1) {
        const char = sample[index]

        if (char === '"') {
            quoted = !quoted
        } else if (!quoted && char === delimiter) {
            count += 1
        } else if (!quoted && char === '\n') {
            counts.push(count)
            count = 0
            if (counts.length >= SAMPLE_LINES) return counts
        }
    }

    if (count > 0 || counts.length === 0) counts.push(count)

    return counts
}

/**
 * Роздільник, який дає однакову ненульову кількість колонок у найбільшій
 * частці рядків зразка; за рівності — той, якого більше. Excel з українською
 * локаллю зберігає CSV через «;», а кома в таких файлах — десятковий знак.
 */
export const detectCsvDelimiter = (text: string): CsvDelimiter => {
    const sample = text.replace(/\r\n?/g, '\n')
    let best: CsvDelimiter = ','
    let bestScore = -1

    for (const delimiter of CANDIDATES) {
        const counts = countPerLine(sample, delimiter)
        const first = counts[0] ?? 0

        if (first === 0) continue

        const consistent = counts.filter((count) => count === first).length / counts.length
        const score = consistent * 1000 + first

        if (score > bestScore) {
            best = delimiter
            bestScore = score
        }
    }

    return best
}

export interface CsvTable {
    delimiter: CsvDelimiter
    rows: string[][]
    /** Скільки рядків у файлі всього (без порожнього хвоста). */
    totalRows: number
    /** Найбільша кількість колонок серед повернених рядків. */
    columns: number
}

/**
 * Розбирає CSV у рядки. `maxRows` обмежує, скільки рядків повертається
 * (таблиця на десятки тисяч рядків гальмує сторінку), але `totalRows`
 * рахує всі — переглядач називає, скільки приховав.
 */
export const parseCsv = (input: string, maxRows = Number.POSITIVE_INFINITY, delimiter?: CsvDelimiter): CsvTable => {
    const text = input.startsWith('﻿') ? input.slice(1) : input
    const separator = delimiter ?? detectCsvDelimiter(text)
    const rows: string[][] = []
    let row: string[] = []
    let field = ''
    let quoted = false
    let totalRows = 0
    let columns = 0

    const pushRow = (): void => {
        row.push(field)
        field = ''

        // Порожній рядок (наприклад, хвостове переведення рядка) не рахуємо
        if (!(row.length === 1 && row[0] === '')) {
            totalRows += 1
            if (rows.length < maxRows) {
                rows.push(row)
                columns = Math.max(columns, row.length)
            }
        }

        row = []
    }

    for (let index = 0; index < text.length; index += 1) {
        const char = text[index]

        if (quoted) {
            if (char === '"') {
                if (text[index + 1] === '"') {
                    field += '"'
                    index += 1
                } else {
                    quoted = false
                }
            } else {
                field += char
            }
            continue
        }

        if (char === '"' && field === '') {
            quoted = true
        } else if (char === separator) {
            row.push(field)
            field = ''
        } else if (char === '\r') {
            if (text[index + 1] === '\n') index += 1
            pushRow()
        } else if (char === '\n') {
            pushRow()
        } else {
            field += char
        }
    }

    if (field !== '' || row.length > 0) pushRow()

    return { delimiter: separator, rows, totalRows, columns }
}
